import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { money } from '../lib/money';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const TABS = ['Overview', 'Giving', 'Pledges', 'Events', 'Campaigns'];
const badge = (s) => ({ received: 'bg-emerald-100 text-emerald-700', pending: 'bg-amber-100 text-amber-700', fulfilled: 'bg-emerald-100 text-emerald-700', partial: 'bg-amber-100 text-amber-700', open: 'bg-slate-100 text-slate-600' }[s] || 'bg-slate-100 text-slate-600');
const inputCls = 'border border-slate-200 rounded-md px-2 py-2 text-xs bg-white w-full';

function DonorPicker({ alumni, value, onChange, name, onName }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <select data-testid="adv-donor-select" value={value} onChange={(e) => onChange(e.target.value)} className={inputCls}>
        <option value="">External donor…</option>
        {(alumni || []).map((a) => <option key={a.student_id} value={a.student_id}>{a.name} ({a.graduation_year || 'alumni'})</option>)}
      </select>
      {!value && <input data-testid="adv-donor-name-input" placeholder="Donor name" value={name} onChange={(e) => onName(e.target.value)} className={inputCls} />}
    </div>
  );
}

function GivingTab({ alumni, pledges, inval }) {
  const qc = useQueryClient();
  const { data: donations } = useQuery({ queryKey: ['adv-donations'], queryFn: () => api.get('/advancement/donations').then((r) => r.data) });
  const { data: giveSettings } = useQuery({ queryKey: ['adv-give-settings'], queryFn: () => api.get('/advancement/give-settings').then((r) => r.data), retry: false });
  const giveMut = useMutation({
    mutationFn: (patch) => api.post('/advancement/give-settings', patch),
    onSuccess: () => { toast.success('Giving page updated'); qc.invalidateQueries({ queryKey: ['adv-give-settings'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const editTaxNote = () => {
    const note = window.prompt('Tax-exemption note shown on receipts and the giving page (e.g. "Donations are eligible for 80G tax exemption - Reg. no. ABC123"):', giveSettings?.tax_note || '');
    if (note !== null) giveMut.mutate({ tax_note: note });
  };
  const [f, setF] = useState({ donor_student_id: '', donor_name: '', donor_email: '', amount: '', method: 'bank', reference: '', pledge_id: '', notes: '' });
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });
  const payload = () => ({ ...f, donor_student_id: f.donor_student_id || null, pledge_id: f.pledge_id || null, amount: parseFloat(f.amount) });
  const recordMut = useMutation({
    mutationFn: () => api.post('/advancement/donations', payload()),
    onSuccess: () => { toast.success('Donation recorded'); setF({ donor_student_id: '', donor_name: '', donor_email: '', amount: '', method: 'bank', reference: '', pledge_id: '', notes: '' }); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const stripeMut = useMutation({
    mutationFn: () => api.post('/advancement/donations/checkout', { ...payload(), origin_url: window.location.origin }),
    onSuccess: (r) => { toast.success('Stripe checkout opened in a new tab'); window.open(r.data.checkout_url, '_blank'); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const valid = f.amount && (f.donor_student_id || f.donor_name);
  return (
    <div>
      {giveSettings && (
        <div className="bg-white border border-slate-200 border-t-4 border-t-orange-500 rounded-md p-4 mb-5" data-testid="adv-give-settings-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="font-mono-label text-[10px] uppercase text-slate-400">Public giving page</div>
              <div className="text-xs text-slate-600 mt-1 break-all" data-testid="adv-give-url">{giveSettings.url}</div>
              {giveSettings.tax_note && <div className="text-[10px] text-slate-400 mt-1">Receipt tax note: {giveSettings.tax_note}</div>}
            </div>
            <div className="flex gap-2 items-center">
              <button data-testid="adv-give-copy-button" onClick={() => { navigator.clipboard.writeText(giveSettings.url); toast.success('Giving link copied'); }}
                className="text-xs border border-slate-300 rounded px-2.5 py-1.5 hover:bg-slate-50">Copy link</button>
              <button data-testid="adv-give-taxnote-button" onClick={editTaxNote} className="text-xs border border-slate-300 rounded px-2.5 py-1.5 hover:bg-slate-50">Tax note</button>
              <button data-testid="adv-give-toggle-button" onClick={() => giveMut.mutate({ enabled: !giveSettings.enabled })}
                className={`text-xs rounded px-2.5 py-1.5 ${giveSettings.enabled ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-600'}`}>
                {giveSettings.enabled ? 'Live' : 'Off'}</button>
            </div>
          </div>
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-md p-4 mb-5" data-testid="adv-donation-form">
        <div className="font-mono-label text-[10px] uppercase text-slate-400 mb-3">Record a gift</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          <div className="lg:col-span-1"><DonorPicker alumni={alumni} value={f.donor_student_id} onChange={(v) => setF({ ...f, donor_student_id: v })} name={f.donor_name} onName={(v) => setF({ ...f, donor_name: v })} /></div>
          <input data-testid="adv-donation-amount-input" type="number" placeholder="Amount" value={f.amount} onChange={set('amount')} className={inputCls} />
          <select data-testid="adv-donation-method-select" value={f.method} onChange={set('method')} className={inputCls}>
            {['bank', 'upi', 'cash', 'cheque'].map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <input data-testid="adv-donation-reference-input" placeholder="Reference no. (optional)" value={f.reference} onChange={set('reference')} className={inputCls} />
          <select data-testid="adv-donation-pledge-select" value={f.pledge_id} onChange={set('pledge_id')} className={inputCls}>
            <option value="">No linked pledge</option>
            {(pledges || []).filter((p) => p.pledge_status !== 'fulfilled').map((p) => <option key={p.id} value={p.id}>{p.donor_name} — {money(p.outstanding, p.currency)} outstanding</option>)}
          </select>
          <input data-testid="adv-donation-email-input" placeholder="Donor email (optional)" value={f.donor_email} onChange={set('donor_email')} className={inputCls} />
        </div>
        <div className="flex gap-2 mt-3">
          <button data-testid="adv-record-donation-button" disabled={!valid || recordMut.isPending} onClick={() => recordMut.mutate()}
            className="text-xs bg-slate-900 text-white rounded px-3 py-2 hover:bg-slate-800 disabled:opacity-40">Record donation</button>
          <button data-testid="adv-stripe-donation-button" disabled={!valid || stripeMut.isPending} onClick={() => stripeMut.mutate()}
            className="text-xs border border-slate-300 rounded px-3 py-2 hover:bg-slate-50 disabled:opacity-40">{stripeMut.isPending ? 'Creating link…' : 'Pay online (Stripe test)'}</button>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto" data-testid="adv-donations-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr className="text-left">
            {['Date', 'Donor', 'Amount', 'Method', 'Receipt', 'Status'].map((h) => <th key={h} className="py-2 px-3 font-mono-label text-[10px] uppercase text-slate-400">{h}</th>)}
          </tr></thead>
          <tbody>
            {(donations || []).map((d, i) => (
              <tr key={d.id} className={`border-t border-slate-100 ${i % 2 ? 'bg-slate-50/50' : ''}`} data-testid={`adv-donation-row-${d.id}`}>
                <td className="py-2 px-3 text-xs">{d.created_at?.slice(0, 10)}</td>
                <td className="py-2 px-3 font-medium">{d.donor_name}</td>
                <td className="py-2 px-3">{money(d.amount, d.currency)}</td>
                <td className="py-2 px-3 text-xs">{d.method}{d.source === 'public' ? ' · web' : ''}</td>
                <td className="py-2 px-3 text-xs">{d.receipt_no ? <span className="font-mono-label" title={d.receipt_email_status}>{d.receipt_no}</span> : '-'}</td>
                <td className="py-2 px-3"><span className={`text-[10px] px-2 py-0.5 rounded-full ${badge(d.status)}`}>{d.status}</span></td>
              </tr>
            ))}
            {(donations || []).length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-400">No donations yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PledgesTab({ alumni, pledges, inval }) {
  const [f, setF] = useState({ donor_student_id: '', donor_name: '', amount: '', due_date: '', notes: '' });
  const createMut = useMutation({
    mutationFn: () => api.post('/advancement/pledges', { ...f, donor_student_id: f.donor_student_id || null, amount: parseFloat(f.amount), due_date: f.due_date || null }),
    onSuccess: () => { toast.success('Pledge recorded'); setF({ donor_student_id: '', donor_name: '', amount: '', due_date: '', notes: '' }); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const cancelMut = useMutation({
    mutationFn: (id) => api.delete(`/advancement/pledges/${id}`),
    onSuccess: () => { toast.success('Pledge cancelled'); inval(); }, onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <div>
      <div className="bg-white border border-slate-200 rounded-md p-4 mb-5" data-testid="adv-pledge-form">
        <div className="font-mono-label text-[10px] uppercase text-slate-400 mb-3">New pledge</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <DonorPicker alumni={alumni} value={f.donor_student_id} onChange={(v) => setF({ ...f, donor_student_id: v })} name={f.donor_name} onName={(v) => setF({ ...f, donor_name: v })} />
          <input data-testid="adv-pledge-amount-input" type="number" placeholder="Pledged amount" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className={inputCls} />
          <input data-testid="adv-pledge-due-input" type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} className={inputCls} />
          <button data-testid="adv-create-pledge-button" disabled={!f.amount || (!f.donor_student_id && !f.donor_name) || createMut.isPending} onClick={() => createMut.mutate()}
            className="text-xs bg-slate-900 text-white rounded px-3 py-2 hover:bg-slate-800 disabled:opacity-40">Record pledge</button>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto" data-testid="adv-pledges-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr className="text-left">
            {['Donor', 'Pledged', 'Fulfilled', 'Outstanding', 'Due', 'Status', ''].map((h, i) => <th key={i} className="py-2 px-3 font-mono-label text-[10px] uppercase text-slate-400">{h}</th>)}
          </tr></thead>
          <tbody>
            {(pledges || []).map((p, i) => (
              <tr key={p.id} className={`border-t border-slate-100 ${i % 2 ? 'bg-slate-50/50' : ''}`} data-testid={`adv-pledge-row-${p.id}`}>
                <td className="py-2 px-3 font-medium">{p.donor_name}</td>
                <td className="py-2 px-3">{money(p.amount, p.currency)}</td>
                <td className="py-2 px-3">{money(p.fulfilled, p.currency)}</td>
                <td className="py-2 px-3">{money(p.outstanding, p.currency)}</td>
                <td className="py-2 px-3 text-xs">{p.due_date || '-'}</td>
                <td className="py-2 px-3"><span className={`text-[10px] px-2 py-0.5 rounded-full ${badge(p.pledge_status)}`}>{p.pledge_status}</span></td>
                <td className="py-2 px-3">{p.pledge_status === 'open' && <button data-testid={`adv-cancel-pledge-${p.id}`} onClick={() => window.confirm('Cancel this pledge?') && cancelMut.mutate(p.id)} className="text-xs underline text-slate-400">cancel</button>}</td>
              </tr>
            ))}
            {(pledges || []).length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-400">No pledges yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EventsTab({ alumni, inval }) {
  const qc = useQueryClient();
  const { data: events } = useQuery({ queryKey: ['adv-events'], queryFn: () => api.get('/advancement/events').then((r) => r.data) });
  const [f, setF] = useState({ title: '', date: '', time: '', location: '', description: '' });
  const [open, setOpen] = useState('');
  const [pick, setPick] = useState('');
  const { data: rsvps } = useQuery({ queryKey: ['adv-rsvps', open], enabled: !!open, queryFn: () => api.get(`/advancement/events/${open}/rsvps`).then((r) => r.data) });
  const invalEv = () => { qc.invalidateQueries({ queryKey: ['adv-events'] }); qc.invalidateQueries({ queryKey: ['adv-rsvps'] }); inval(); };
  const createMut = useMutation({
    mutationFn: () => api.post('/advancement/events', f),
    onSuccess: () => { toast.success('Alumni event created'); setF({ title: '', date: '', time: '', location: '', description: '' }); invalEv(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const rsvpMut = useMutation({
    mutationFn: ({ eid, going }) => api.post(`/advancement/events/${eid}/rsvp`, { student_id: pick, going }),
    onSuccess: () => { toast.success('RSVP recorded'); setPick(''); invalEv(); }, onError: (e) => toast.error(errMsg(e)),
  });
  const delMut = useMutation({
    mutationFn: (eid) => api.delete(`/advancement/events/${eid}`),
    onSuccess: () => { toast.success('Event deleted'); setOpen(''); invalEv(); }, onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <div>
      <div className="bg-white border border-slate-200 rounded-md p-4 mb-5" data-testid="adv-event-form">
        <div className="font-mono-label text-[10px] uppercase text-slate-400 mb-3">New alumni event</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
          <input data-testid="adv-event-title-input" placeholder="Title (e.g. Batch of 2015 Reunion)" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inputCls} />
          <input data-testid="adv-event-date-input" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={inputCls} />
          <input data-testid="adv-event-time-input" placeholder="Time (e.g. 6:00 PM)" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} className={inputCls} />
          <input data-testid="adv-event-location-input" placeholder="Location" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} className={inputCls} />
          <button data-testid="adv-create-event-button" disabled={!f.title || !f.date || createMut.isPending} onClick={() => createMut.mutate()}
            className="text-xs bg-slate-900 text-white rounded px-3 py-2 hover:bg-slate-800 disabled:opacity-40">Create event</button>
        </div>
      </div>
      <div className="space-y-3" data-testid="adv-events-list">
        {(events || []).map((e) => (
          <div key={e.id} className="bg-white border border-slate-200 rounded-md p-4" data-testid={`adv-event-card-${e.id}`}>
            <div className="flex items-start justify-between flex-wrap gap-2">
              <div>
                <div className="font-semibold">{e.title}</div>
                <div className="text-xs text-slate-500">{e.date}{e.time ? ` · ${e.time}` : ''}{e.location ? ` · ${e.location}` : ''}</div>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-emerald-700">{e.going} going</span>
                <span className="text-slate-400">{e.declined} declined</span>
                <button data-testid={`adv-event-rsvplink-${e.id}`} onClick={() => { navigator.clipboard.writeText(e.rsvp_url); toast.success('Self-RSVP link copied - share it with alumni'); }} className="underline text-orange-700">Copy RSVP link</button>
                <button data-testid={`adv-event-rsvps-${e.id}`} onClick={() => setOpen(open === e.id ? '' : e.id)} className="underline text-orange-700">{open === e.id ? 'hide RSVPs' : 'RSVPs'}</button>
                <button data-testid={`adv-event-delete-${e.id}`} onClick={() => window.confirm('Delete this event?') && delMut.mutate(e.id)} className="underline text-slate-400">delete</button>
              </div>
            </div>
            {open === e.id && (
              <div className="mt-3 border-t border-slate-100 pt-3">
                <div className="flex gap-2 items-center mb-2 flex-wrap">
                  <select data-testid="adv-rsvp-alumni-select" value={pick} onChange={(ev) => setPick(ev.target.value)} className="border border-slate-200 rounded-md px-2 py-1.5 text-xs bg-white max-w-[240px]">
                    <option value="">Record RSVP for…</option>
                    {(alumni || []).map((a) => <option key={a.student_id} value={a.student_id}>{a.name} ({a.graduation_year || 'alumni'})</option>)}
                  </select>
                  <button data-testid="adv-rsvp-going-button" disabled={!pick || rsvpMut.isPending} onClick={() => rsvpMut.mutate({ eid: e.id, going: true })} className="text-xs bg-emerald-600 text-white rounded px-2 py-1.5 disabled:opacity-40">Going</button>
                  <button data-testid="adv-rsvp-decline-button" disabled={!pick || rsvpMut.isPending} onClick={() => rsvpMut.mutate({ eid: e.id, going: false })} className="text-xs border border-slate-300 rounded px-2 py-1.5 disabled:opacity-40">Declined</button>
                </div>
                <div className="text-xs text-slate-600 space-y-1">
                  {(rsvps || []).map((r) => <div key={r.id}>{r.name} — <span className={r.going ? 'text-emerald-700' : 'text-slate-400'}>{r.going ? 'going' : 'declined'}</span>{r.source === 'self' && <span className="text-[10px] text-orange-600 ms-1">(self)</span>}</div>)}
                  {(rsvps || []).length === 0 && <div className="text-slate-400">No RSVPs recorded yet.</div>}
                </div>
              </div>
            )}
          </div>
        ))}
        {(events || []).length === 0 && <div className="p-6 text-center text-slate-400 bg-white border border-slate-200 rounded-md">No alumni events yet.</div>}
      </div>
    </div>
  );
}

function CampaignsTab({ alumni, inval }) {
  const { data: campaigns } = useQuery({ queryKey: ['adv-campaigns'], queryFn: () => api.get('/advancement/campaigns').then((r) => r.data) });
  const [f, setF] = useState({ name: '', subject: '', body: '', batch_year: '' });
  const years = [...new Set((alumni || []).map((a) => a.graduation_year).filter(Boolean))].sort().reverse();
  const sendMut = useMutation({
    mutationFn: () => api.post('/advancement/campaigns', { ...f, batch_year: f.batch_year || null }),
    onSuccess: (r) => { toast.success(`Campaign sent to ${r.data.recipients} alumni (${r.data.email_status})`); setF({ name: '', subject: '', body: '', batch_year: '' }); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <div>
      <div className="bg-white border border-slate-200 rounded-md p-4 mb-5" data-testid="adv-campaign-form">
        <div className="font-mono-label text-[10px] uppercase text-slate-400 mb-3">New engagement campaign (email)</div>
        <div className="grid sm:grid-cols-3 gap-2 mb-2">
          <input data-testid="adv-campaign-name-input" placeholder="Campaign name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} />
          <input data-testid="adv-campaign-subject-input" placeholder="Email subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} className={inputCls} />
          <select data-testid="adv-campaign-batch-select" value={f.batch_year} onChange={(e) => setF({ ...f, batch_year: e.target.value })} className={inputCls}>
            <option value="">All batches</option>
            {years.map((y) => <option key={y} value={y}>Batch of {y}</option>)}
          </select>
        </div>
        <textarea data-testid="adv-campaign-body-input" placeholder="Message to alumni…" rows={4} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className={`${inputCls} resize-y`} />
        <button data-testid="adv-send-campaign-button" disabled={!f.name || !f.subject || !f.body || sendMut.isPending} onClick={() => sendMut.mutate()}
          className="mt-2 text-xs bg-slate-900 text-white rounded px-3 py-2 hover:bg-slate-800 disabled:opacity-40">{sendMut.isPending ? 'Sending…' : 'Send campaign'}</button>
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto" data-testid="adv-campaigns-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr className="text-left">
            {['Date', 'Campaign', 'Segment', 'Audience', 'Emailed', 'Delivery'].map((h) => <th key={h} className="py-2 px-3 font-mono-label text-[10px] uppercase text-slate-400">{h}</th>)}
          </tr></thead>
          <tbody>
            {(campaigns || []).map((c, i) => (
              <tr key={c.id} className={`border-t border-slate-100 ${i % 2 ? 'bg-slate-50/50' : ''}`} data-testid={`adv-campaign-row-${c.id}`}>
                <td className="py-2 px-3 text-xs">{c.created_at?.slice(0, 10)}</td>
                <td className="py-2 px-3 font-medium">{c.name}</td>
                <td className="py-2 px-3 text-xs">{c.batch_year ? `Batch of ${c.batch_year}` : 'All alumni'}</td>
                <td className="py-2 px-3">{c.audience}</td>
                <td className="py-2 px-3">{c.recipients}</td>
                <td className="py-2 px-3 text-xs">{c.email_status}</td>
              </tr>
            ))}
            {(campaigns || []).length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-400">No campaigns sent yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Advancement() {
  const qc = useQueryClient();
  const [tab, setTab] = useState('Overview');
  const { data: summary, error: summaryError } = useQuery({ queryKey: ['adv-summary'], queryFn: () => api.get('/advancement/summary').then((r) => r.data), retry: false });
  const { data: alumni } = useQuery({ queryKey: ['alumni'], queryFn: () => api.get('/alumni').then((r) => r.data), retry: false });
  const { data: pledges } = useQuery({ queryKey: ['adv-pledges'], queryFn: () => api.get('/advancement/pledges').then((r) => r.data) });
  const { data: donations } = useQuery({ queryKey: ['adv-donations'], queryFn: () => api.get('/advancement/donations').then((r) => r.data) });
  const inval = () => ['adv-summary', 'adv-donations', 'adv-pledges', 'adv-events', 'adv-campaigns'].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const cur = (donations || [])[0]?.currency || (pledges || [])[0]?.currency || 'INR';
  const cards = [
    { label: 'Total raised', value: money(summary?.raised, cur), test: 'adv-card-raised' },
    { label: 'Raised this year', value: money(summary?.raised_this_year, cur), test: 'adv-card-year' },
    { label: 'Donors', value: summary?.donors ?? 0, test: 'adv-card-donors' },
    { label: 'Pledged outstanding', value: money(summary?.pledge_outstanding, cur), test: 'adv-card-outstanding' },
  ];
  if (summaryError?.response?.status === 403) {
    return (
      <div data-testid="advancement-denied" className="bg-white border border-slate-200 rounded-md p-10 text-center text-slate-500">
        Advancement is restricted to administration and finance roles.
      </div>
    );
  }
  return (
    <div data-testid="advancement-page">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-1">Advancement</h1>
      <p className="text-sm text-slate-500 mb-6">Alumni giving, pledges, reunions and engagement — built on the same alumni registry.</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {cards.map((c) => (
          <div key={c.label} className="bg-white border border-slate-200 rounded-md p-4" data-testid={c.test}>
            <div className="font-mono-label text-[10px] uppercase text-slate-400">{c.label}</div>
            <div className="text-xl font-bold mt-1">{c.value}</div>
          </div>
        ))}
      </div>
      <div className="flex gap-1 mb-5 border-b border-slate-200 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t} data-testid={`adv-tab-${t.toLowerCase()}`} onClick={() => setTab(t)}
            className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 -mb-px ${tab === t ? 'border-slate-900 font-semibold' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>{t}</button>
        ))}
      </div>
      {tab === 'Overview' && (
        <div className="bg-white border border-slate-200 rounded-md overflow-x-auto" data-testid="adv-overview-recent">
          <div className="px-4 pt-3 font-mono-label text-[10px] uppercase text-slate-400">Recent gifts</div>
          <table className="w-full text-sm">
            <tbody>
              {(donations || []).slice(0, 8).map((d) => (
                <tr key={d.id} className="border-t border-slate-100">
                  <td className="py-2 px-4 text-xs">{d.created_at?.slice(0, 10)}</td>
                  <td className="py-2 px-4 font-medium">{d.donor_name}</td>
                  <td className="py-2 px-4">{money(d.amount, d.currency)}</td>
                  <td className="py-2 px-4"><span className={`text-[10px] px-2 py-0.5 rounded-full ${badge(d.status)}`}>{d.status}</span></td>
                </tr>
              ))}
              {(donations || []).length === 0 && <tr><td className="p-6 text-center text-slate-400">No gifts recorded yet — head to the Giving tab.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {tab === 'Giving' && <GivingTab alumni={alumni} pledges={pledges} inval={inval} />}
      {tab === 'Pledges' && <PledgesTab alumni={alumni} pledges={pledges} inval={inval} />}
      {tab === 'Events' && <EventsTab alumni={alumni} inval={inval} />}
      {tab === 'Campaigns' && <CampaignsTab alumni={alumni} inval={inval} />}
    </div>
  );
}
